import { useState, useRef, useCallback } from 'react';

const ARIA_CONVERSATION_LOG_KEY = 'aria_conversation_log';
const ARIA_CONVERSATION_ARCHIVE_KEY = 'aria_conversation_archive';
const CONVERSATION_DAY_MS = 24 * 60 * 60 * 1000;

function readConversationLog() {
  try {
    const raw = localStorage.getItem(ARIA_CONVERSATION_LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.startedAt) return [];
    if (Date.now() - parsed.startedAt > CONVERSATION_DAY_MS) {
      archiveConversationLog(parsed);
      return [];
    }
    return parsed.entries || [];
  } catch (_) { return []; }
}

function writeConversationLog(entries) {
  try {
    const existing = localStorage.getItem(ARIA_CONVERSATION_LOG_KEY);
    let startedAt = Date.now();
    if (existing) {
      const parsed = JSON.parse(existing);
      if (parsed && parsed.startedAt && (Date.now() - parsed.startedAt < CONVERSATION_DAY_MS)) {
        startedAt = parsed.startedAt;
      }
    }
    localStorage.setItem(ARIA_CONVERSATION_LOG_KEY, JSON.stringify({ startedAt, entries }));
  } catch (_) {}
}

function archiveConversationLog(dayLog) {
  try {
    const raw = localStorage.getItem(ARIA_CONVERSATION_ARCHIVE_KEY);
    const archive = raw ? JSON.parse(raw) : [];
    const dateStr = new Date(dayLog.startedAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    archive.push({ date: dateStr, startedAt: dayLog.startedAt, entries: dayLog.entries || [] });
    while (archive.length > 30) archive.shift();
    localStorage.setItem(ARIA_CONVERSATION_ARCHIVE_KEY, JSON.stringify(archive));
  } catch (_) {}
}

function readConversationArchive() {
  try {
    const raw = localStorage.getItem(ARIA_CONVERSATION_ARCHIVE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) { return []; }
}

export default function useConversationLog() {
  const [bubbleText, setBubbleText] = useState('');
  const [bubbleRole, setBubbleRole] = useState('');
  const [conversationLog, setConversationLog] = useState(() => readConversationLog());
  const [showConversationOverlay, setShowConversationOverlay] = useState(false);
  const [showConversationHistory, setShowConversationHistory] = useState(false);
  const [selectedArchiveDay, setSelectedArchiveDay] = useState(null);
  const [conversationArchive, setConversationArchive] = useState(() => readConversationArchive());
  const bubbleTimeoutRef = useRef(null);

  const [ariaBubbleText, setAriaBubbleText] = useState('');
  const [userBubbleText, setUserBubbleText] = useState('');
  const ariaBubbleTimerRef = useRef(null);
  const userBubbleTimerRef = useRef(null);

  const addConversationEntry = useCallback((role, text) => {
    if (!text || !text.trim()) return;
    const entry = { role, text: text.trim(), time: Date.now() };
    setConversationLog(prev => {
      const next = [...prev, entry];
      writeConversationLog(next);
      return next;
    });
    if (role === 'user') {
      setUserBubbleText(text.trim());
      if (userBubbleTimerRef.current) clearTimeout(userBubbleTimerRef.current);
      userBubbleTimerRef.current = setTimeout(() => setUserBubbleText(''), 5000);
    } else {
      setAriaBubbleText(text.trim());
      if (ariaBubbleTimerRef.current) clearTimeout(ariaBubbleTimerRef.current);
      ariaBubbleTimerRef.current = setTimeout(() => setAriaBubbleText(''), 6000);
    }
  }, []);

  // ── THE WHISPER BUBBLES ARE GONE (Braeden 9/2) ─────────────────────────
  // "i dont want to see the whisper bubbles anymore, get rid of it." This
  // used to float what the ear was hearing - partials and the sent line -
  // into the user bubble at the top right. It flickered with half-heard
  // words and was the most visible thing the ear got wrong. The callers are
  // untouched; the bubble simply no longer shows.
  const SHOW_WHISPER_BUBBLES = false;
  const showLiveInBubble = useCallback((text) => {
    if (!SHOW_WHISPER_BUBBLES) return;
    if (!text || !text.trim()) return;
    setUserBubbleText(text.trim());
    if (userBubbleTimerRef.current) clearTimeout(userBubbleTimerRef.current);
  }, []);

  const refreshArchive = useCallback(() => {
    setConversationArchive(readConversationArchive());
  }, []);

  return {
    bubbleText, setBubbleText,
    bubbleRole, setBubbleRole,
    conversationLog, setConversationLog,
    showConversationOverlay, setShowConversationOverlay,
    showConversationHistory, setShowConversationHistory,
    selectedArchiveDay, setSelectedArchiveDay,
    conversationArchive, setConversationArchive,
    ariaBubbleText, setAriaBubbleText,
    userBubbleText, setUserBubbleText,
    addConversationEntry,
    showLiveInBubble,
    refreshArchive,
  };
}
